// Special case: tilt 0° is always 0.87 regardless of orientation
const TILT_0_FACTOR = 0.87

const ORIENTATION_MAP = {
  SW: 'zuid-west',
  SE: 'zuid-oost',
  S: 'zuid',
  N: 'noord',
  NW: 'noord-west',
  NE: 'noord-oost',
  E: 'oost',
  W: 'west',
}

// Solar Distribution Factor lookup table
const SOLAR_DISTRIBUTION_MAP = {
  '10-noord': 0.79,
  '20-noord': 0.7,
  '30-noord': 0.61,
  '40-noord': 0.52,
  '50-noord': 0.43,
  '60-noord': 0.35,
  '70-noord': 0.28,
  '80-noord': 0.23,
  '90-noord': 0.18,

  '10-noord-oost': 0.81,
  '20-noord-oost': 0.74,
  '30-noord-oost': 0.67,
  '40-noord-oost': 0.61,
  '50-noord-oost': 0.54,
  '60-noord-oost': 0.48,
  '70-noord-oost': 0.41,
  '80-noord-oost': 0.35,
  '90-noord-oost': 0.33,

  '10-oost': 0.89,
  '20-oost': 0.87,
  '30-oost': 0.86,
  '40-oost': 0.82,
  '50-oost': 0.78,
  '60-oost': 0.74,
  '70-oost': 0.69,
  '80-oost': 0.63,
  '90-oost': 0.56,

  '10-zuid-oost': 0.94,
  '20-zuid-oost': 0.96,
  '30-zuid-oost': 0.96,
  '40-zuid-oost': 0.95,
  '50-zuid-oost': 0.92,
  '60-zuid-oost': 0.87,
  '70-zuid-oost': 0.82,
  '80-zuid-oost': 0.75,
  '90-zuid-oost': 0.67,

  '10-zuid': 0.96,
  '20-zuid': 0.98,
  '30-zuid': 1.0,
  '40-zuid': 1.0,
  '50-zuid': 0.97,
  '60-zuid': 0.93,
  '70-zuid': 0.87,
  '80-zuid': 0.82,
  '90-zuid': 0.71,

  '10-zuid-west': 0.94,
  '20-zuid-west': 0.96,
  '30-zuid-west': 0.96,
  '40-zuid-west': 0.96,
  '50-zuid-west': 0.93,
  '60-zuid-west': 0.89,
  '70-zuid-west': 0.84,
  '80-zuid-west': 0.77,
  '90-zuid-west': 0.69,

  '10-west': 0.9,
  '20-west': 0.88,
  '30-west': 0.86,
  '40-west': 0.84,
  '50-west': 0.8,
  '60-west': 0.76,
  '70-west': 0.7,
  '80-west': 0.65,
  '90-west': 0.58,

  '10-noord-west': 0.81,
  '20-noord-west': 0.74,
  '30-noord-west': 0.67,
  '40-noord-west': 0.61,
  '50-noord-west': 0.54,
  '60-noord-west': 0.48,
  '70-noord-west': 0.41,
  '80-noord-west': 0.35,
  '90-noord-west': 0.33,
}

export function extractPanelPower(panelName) {
  if (!panelName) return 0
  // Match 2-4 consecutive digits that could be:
  // - followed by W/Wp/wp (with optional space)
  // - at a word boundary (not followed by more digits)
  const match = panelName.match(/\b(\d{2,4})(?:\s*(?:Wp?|wp?|WP|W))?(?:\s|$|-|[^\d])/)
  return match ? parseInt(match[1], 10) : 0
}

export function calculateTotalPanelPower(products) {
  if (!products || !Array.isArray(products)) {
    return 0
  }

  let totalPower = 0

  products
    .filter((product) => product.type === 'panel')
    .forEach((product) => {
      const amount = parseInt(product.amount, 10) || 0
      const power = product.power
        ? parseInt(product.power, 10)
        : extractPanelPower(product.description)
      totalPower += amount * power
    })

  return totalPower
}

function getSolarDistributionFactor(tilt, orientation) {
  if (tilt === 0) {
    return TILT_0_FACTOR
  }

  const roundedTilt = Math.floor(tilt / 10) * 10
  const normalizedOrientation = (orientation || '').toLowerCase().trim()
  const mappedOrientation = ORIENTATION_MAP[orientation?.toUpperCase()] || normalizedOrientation
  const key = `${roundedTilt}-${mappedOrientation}`

  return SOLAR_DISTRIBUTION_MAP[key] || 0.87
}

export function calculateExpectedProduction(products, extraFields, basePowerFactor = 1000) {
  const panelPower = calculateTotalPanelPower(products)

  if (panelPower === 0) {
    return 0
  }

  const tilt = extraFields?.roof_angle ? parseFloat(extraFields.roof_angle) : 0
  const orientation = extraFields?.roof_orientation || 'S'

  // If no shadow_loss, use default factor of 1 (no loss)
  const shadowLossPercentage = extraFields?.shadow_loss ? parseFloat(extraFields.shadow_loss) : 0
  const shadowLossFactor = 1 - shadowLossPercentage / 100
  const angularSolarFactor = getSolarDistributionFactor(tilt, orientation)

  return Math.floor(panelPower * angularSolarFactor * basePowerFactor * shadowLossFactor * 0.001)
}

export function calculateRatio(expectedProduction, totalPanelPower) {
  if (!totalPanelPower || totalPanelPower === 0) {
    return 0
  }
  // Ratio is expected production divided by total panel power, expressed as percentage
  return Math.round((expectedProduction / totalPanelPower) * 100 * 100) / 100
}
